import { map } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { IconName, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { TooltippedIcon, LocalizedDateFromUtc } from '@mews-commander/ui';
import { LocalizedText, withAppClockService } from '@mews/framework';
import { mapCustomerClassifications, notEmpty } from '@mews-commander/utils';
import { getCurrentEnterpriseReservationCustomerId } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getCustomer } from '@mews-commander/legacy-entities/src/ducks/customers';
import { updateOwner as updateOwnerAction } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { hasCustomerValidExternalCreditCard } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { deleteCompanionship, unassignCompanionship } from '@mews-commander/legacy-entities/src/ducks/companionships';
import { getCustomerRelatedNotesString } from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import { withActiveWindowLoader } from '../../../core';
import { openBillingAccount, openPayments, refetchGroupAndShowSuccessFlash } from '../actions';
import { editInternal as editInternalsAction } from '../actions/profile';
import ClassificationIcon from '../../../components/commander-ui/ClassificationIcon';
import { CustomerLink } from '../../../components/commander-ui';
import { useCustomerHasBirthdayDuringReservation } from './useCustomerHasBirthdayDuringReservation';

const CompanionListItem = ({
    customer,
    accountNotes,
    hasValidExternalCreditCard,
    customerId,
    reservationCustomerId,
    openBillingScreen,
    shouldDisplayGroupAction,
    editInternals,
    updateOwner,
    unassignCompanion,
    deleteCompanion,
    openPaymentsScreen,
    reservationId,
}: $TSFixMe) => {
    const hasBirthDay = useCustomerHasBirthdayDuringReservation(customerId, reservationId);

    if (!customer) {
        return null;
    }
    const { classifications: customerClassification } = customer;
    const classifications = mapCustomerClassifications(customerClassification);

    const shouldDisplayMakeOwner = shouldDisplayGroupAction && customerId !== reservationCustomerId;

    return (
        <Stack itemAlignment="center" spacing="100" wrap inline>
            <span>
                <TooltippedIcon value={IconName.PaymentReport} tooltip={TextKey.Billing} onClick={openBillingScreen} />
                {hasValidExternalCreditCard ? (
                    <TooltippedIcon
                        value={IconName.PaymentCard}
                        tooltip={TextKey.PaymentCard}
                        onClick={openPaymentsScreen}
                    />
                ) : null}
                {shouldDisplayGroupAction ? (
                    <>
                        <TooltippedIcon
                            value={IconName.ChevronLeft}
                            tooltip={TextKey.Move}
                            onClick={unassignCompanion}
                        />
                        <TooltippedIcon
                            value={IconName.CrossClose}
                            tooltip={TextKey.Remove}
                            onClick={deleteCompanion}
                        />
                    </>
                ) : null}
                {shouldDisplayMakeOwner ? (
                    <TooltippedIcon
                        value={IconName.Share}
                        tooltip={TextKey.ReservationMakeOwner}
                        onClick={updateOwner}
                    />
                ) : null}
            </span>
            <CustomerLink id={customerId} />
            {notEmpty(classifications)
                ? map(
                      (classification) => (
                          <ClassificationIcon
                              onClick={editInternals}
                              key={classification.value}
                              classification={classification}
                          />
                      ),
                      classifications
                  )
                : null}
            {accountNotes ? (
                <TooltippedIcon value={IconName.Notes} tooltip={accountNotes} onClick={editInternals} />
            ) : null}
            {hasBirthDay ? (
                <TooltippedIcon
                    value={IconName.Birthday}
                    tooltip={
                        <>
                            <LocalizedText textKey={TextKey.DateOfBirth} />
                            {UnicodeEntity.Space}
                            <LocalizedDateFromUtc utcString={customer.birthDate} />
                        </>
                    }
                />
            ) : null}
        </Stack>
    );
};

export default pipe(
    CompanionListItem,
    connect(
        createStructuredSelector({
            customer: (state: $TSFixMe, { customerId }: $TSFixMe) => getCustomer(state, { id: customerId }),
            accountNotes: (state: $TSFixMe, { customerId }: $TSFixMe) =>
                getCustomerRelatedNotesString(state, { accountId: customerId, id: customerId }),
            hasValidExternalCreditCard: (state: $TSFixMe, { customerId, appClockService }: $TSFixMe) =>
                hasCustomerValidExternalCreditCard(state, { accountId: customerId, appClockService }),
            reservationCustomerId: (state: $TSFixMe, { reservationId }: $TSFixMe) =>
                getCurrentEnterpriseReservationCustomerId(state, { id: reservationId }),
        }),
        (dispatch, { refetchGroup, customerId: accountId, reservationId, companionshipId }) => ({
            // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void, ApplicationState>' is not assignable to parameter of type 'Action<any>'...
            editInternals: () => dispatch(editInternalsAction({ accountId, onClose: refetchGroup })),
            // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe) => any' is not assignable to parameter of type 'Action<any>'.
            openBillingScreen: () => dispatch(openBillingAccount({ accountId })),
            // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe, { resolveRoute }: $TSFixMe) => any' is not assignable to para...
            openPaymentsScreen: () => dispatch(openPayments({ accountId })),
            updateOwner: async () =>
                // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void, ApplicationState>' is not assignable to parameter of type 'Action<any>'...
                withActiveWindowLoader(() => dispatch(updateOwnerAction(reservationId, accountId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.SaveSuccessMessage })
                ),
            deleteCompanion: async () =>
                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe, { container }: $TSFixMe) => any' is not assignable to paramet...
                withActiveWindowLoader(() => dispatch(deleteCompanionship(companionshipId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.CompanionRemoved })
                ),
            unassignCompanion: async () =>
                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe, { container }: $TSFixMe) => any' is not assignable to paramet...
                withActiveWindowLoader(() => dispatch(unassignCompanionship(companionshipId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.Unassigned })
                ),
        })
    ),
    withAppClockService
);
